<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import draggable from 'vuedraggable';
import DragHandle from '../../common/DragHandle';
import FieldTypeIcon from "./FieldTypeIcon.vue";
import Button from "../Button.vue";

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const router = proxy.$router;

// --- State ---

const isDragging = ref(false);
const ajaxInProgress = ref(false);
const dragImg = ref(null);

// Initialize blank drag image
const initDragImg = () => {
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    dragImg.value = img;
};
initDragImg();

// --- Computed ---

const dragOptions = computed(() => ({
    animation: 200,
    ghostClass: 'ghost',
    setData: (dataTransfer) => {
        if (dragImg.value) {
            dataTransfer.setDragImage(dragImg.value, 0, 0);
        }
    }
}));

const selectedId = computed(() => {
    return store.getters['componentFields/isContentType']
        ? store.state.componentFields.activeEditId
        : parseInt(proxy.$route.params.fieldId);
});

const isContentTypeComponent = computed(() => {
    return store.getters['componentFields/isContentType'];
});

const items = computed({
    get: () => store.state.componentFields.items,
    set: (value) => store.commit('componentFields/items', value)
});

// --- Methods ---

const editItem = (id) => {
    if (isContentTypeComponent.value) {
        store.commit('componentFields/activeEditId', id);
    } else {
        router.push({
            name: 'componentFields',
            params: {
                fieldsetId: store.state.componentFields.activeFieldsetId || 0,
                fieldId: id
            }
        });
    }
};

const onDragEnd = (obj) => {
    isDragging.value = false;
    const item = items.value[obj.newIndex];

    ajaxInProgress.value = true;
    store.dispatch('itemData/update', {
            tableName: 'modulestructure',
            id: item.id,
            data: { displayOrder: obj.newIndex + 1 }
        })
        .then(() => {
            return store.dispatch('componentStructure/init');
        })
        .finally(() => {
            ajaxInProgress.value = false;
        });
};
</script>

<template>
    <div :class="{ 'content-type--items-is-dragging': isDragging }">
        <draggable
            v-if="items.length"
            v-model="items"
            direction="vertical"
            draggable="[data-draggable-item]"
            v-bind="dragOptions"
            @end="onDragEnd"
            @start="isDragging = true"
        >
            <transition-group class="overflow-hidden" type="transition">
                <Button
                    v-for="item in items"
                    :key="item.id"
                    ref="contentTypeItem"
                    :disabled="ajaxInProgress"
                    :active-class="'btn-primary'"
                    :active-value="item.id === selectedId"
                    class="ps-0 btn-sm w-100 d-flex align-items-top text-start"
                    data-draggable-item
                    draggable="true"
                    @click.prevent.native="editItem(item.id)"
                >
                    <DragHandle class="content-type--item-drag-handle"/>

                    <FieldTypeIcon :type="item.type" class="me-2"/>

                    {{ item.title }}
                </Button>
            </transition-group>
        </draggable>
    </div>
</template>

<style scoped>
.ghost {
    background: #666 !important;
    border-color: #666 !important;
    color: #fff !important;
}

.content-type--item-drag-handle {
    opacity: 0;
    color: #999;
}

.btn:hover .content-type--item-drag-handle {
    opacity: 1;
}

.content-type--items-is-dragging .content-type--item-drag-handle {
    opacity: 0 !important;
}
</style>